import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { X } from "lucide-react";
import { cloneElement, forwardRef, isValidElement, useId, useRef, useState, } from "react";
import { clsx } from "clsx";
export function cx(...values) {
    return clsx(values);
}
/**
 * Native, dependency-free Drag-and-Drop behaviour for Kanban cards.
 * The hook is intentionally domain-agnostic, so every module can supply its
 * own cards, columns and persistence callback.
 */
export function useKanbanDragDrop({ getItemId, onMove, disabled = false, }) {
    const draggedItem = useRef(null);
    const [draggingId, setDraggingId] = useState(null);
    const [dropTargetId, setDropTargetId] = useState(null);
    function reset() {
        draggedItem.current = null;
        setDraggingId(null);
        setDropTargetId(null);
    }
    function getDraggableProps(item) {
        const itemId = getItemId(item);
        return {
            draggable: !disabled && Boolean(itemId),
            onDragStart: (event) => {
                if (disabled || !itemId)
                    return;
                draggedItem.current = item;
                setDraggingId(itemId);
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", itemId);
            },
            onDragEnd: reset,
        };
    }
    function getDropZoneProps(targetColumnId) {
        return {
            onDragOver: (event) => {
                if (disabled || !draggedItem.current)
                    return;
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
                setDropTargetId(targetColumnId);
            },
            onDragLeave: (event) => {
                const nextTarget = event.relatedTarget;
                if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget))
                    return;
                setDropTargetId((current) => (current === targetColumnId ? null : current));
            },
            onDrop: (event) => {
                event.preventDefault();
                const item = draggedItem.current;
                if (!disabled && item)
                    onMove(item, targetColumnId);
                reset();
            },
        };
    }
    return { draggingId, dropTargetId, getDraggableProps, getDropZoneProps };
}
export const Button = forwardRef(function Button({ className, variant = "primary", size = "medium", busy, disabled, children, ...props }, ref) {
    return (_jsxs("button", { ref: ref, className: cx("ui-button", `ui-button--${variant}`, `ui-button--${size}`, className), disabled: disabled || busy, "aria-busy": busy || undefined, ...props, children: [busy ? _jsx("span", { className: "ui-spinner ui-spinner--button", "aria-hidden": "true" }) : null, children] }));
});
export const IconButton = forwardRef(function IconButton({ className, children, ...props }, ref) {
    return (_jsx("button", { ref: ref, className: cx("ui-icon-button", className), ...props, children: children }));
});
export function Card({ className, ...props }) {
    return _jsx("div", { className: cx("ui-card", className), ...props });
}
export function Badge({ tone = "neutral", className, ...props }) {
    return _jsx("span", { className: cx("ui-badge", `ui-badge--${tone}`, className), ...props });
}
/**
 * Prüft, ob `node` sein einzelnes Kind-Element ist, dessen Props verlässlich
 * bis zum eigentlichen Formularsteuerelement durchgereicht werden (z. B. die
 * `Input`/`Select`/`Textarea`-Komponenten dieses Pakets, die `...props` auf
 * das native Element spreaden). Reine Host-Wrapper wie `<div class="password-input">`
 * geben Zusatz-Props nicht an das enthaltene Feld weiter — ARIA-Attribute
 * würden dort am falschen Element landen und werden daher nicht geklont.
 */
function isCloneableFieldChild(node) {
    if (!isValidElement(node))
        return false;
    const type = node.type;
    if (typeof type === "string") {
        return type === "input" || type === "select" || type === "textarea";
    }
    return typeof type === "function" || typeof type === "object";
}
export function Field({ label, htmlFor, hint, error, required, children }) {
    const descriptionId = `${htmlFor}-description`;
    const hasDescription = Boolean(error || hint);
    let describedChild = children;
    if (isCloneableFieldChild(children)) {
        const childProps = children.props;
        const extraProps = {};
        if (hasDescription) {
            const existingDescribedBy = childProps["aria-describedby"];
            extraProps["aria-describedby"] = existingDescribedBy
                ? `${existingDescribedBy} ${descriptionId}`
                : descriptionId;
        }
        if (error && childProps["aria-invalid"] === undefined) {
            extraProps["aria-invalid"] = true;
        }
        if (required && childProps["aria-required"] === undefined) {
            extraProps["aria-required"] = true;
        }
        if (Object.keys(extraProps).length > 0) {
            describedChild = cloneElement(children, extraProps);
        }
    }
    return (_jsxs("div", { className: cx("ui-field", error && "ui-field--error"), children: [_jsxs("label", { className: "ui-field__label", htmlFor: htmlFor, children: [label, required ? (_jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": "true", children: " *" }), _jsx("span", { className: "ui-sr-only", children: " Pflichtfeld" })] })) : null] }), describedChild, hasDescription ? (_jsx("p", { className: "ui-field__description", id: descriptionId, role: error ? "alert" : undefined, children: error ?? hint })) : null] }));
}
export const Input = forwardRef(function Input({ className, ...props }, ref) {
    return _jsx("input", { ref: ref, className: cx("ui-input", className), ...props });
});
export const Select = forwardRef(function Select({ className, children, ...props }, ref) {
    return (_jsx("select", { ref: ref, className: cx("ui-input", "ui-select", className), ...props, children: children }));
});
export const Textarea = forwardRef(function Textarea({ className, ...props }, ref) {
    return _jsx("textarea", { ref: ref, className: cx("ui-input", "ui-textarea", className), ...props });
});
function DialogHandlerFooter({ footer, destructiveAction, secondaryActions, cancelAction, primaryAction, }) {
    const hasStructuredActions = Boolean(destructiveAction || secondaryActions || cancelAction || primaryAction);
    if (!hasStructuredActions) {
        return footer ? _jsx("div", { className: "ui-dialog__footer", children: footer }) : null;
    }
    return (_jsxs("div", { className: "ui-dialog__footer ui-dialog__footer--structured", children: [destructiveAction ? (_jsx("div", { className: "ui-dialog__footer-destructive", children: destructiveAction })) : null, _jsxs("div", { className: "ui-dialog__footer-actions", children: [secondaryActions, cancelAction, primaryAction] })] }));
}
export function DialogHandler({ open, onOpenChange, title, description, children, footer, size = "medium", onOpenAutoFocus, onCloseAutoFocus, presentation = "dialog", destructiveAction, secondaryActions, cancelAction, primaryAction, error, }) {
    const pageTitleId = useId();
    const pageDescriptionId = useId();
    const body = (_jsxs("div", { className: "ui-dialog__body", children: [error ? (_jsx("div", { className: "ui-dialog__error", role: "alert", children: error })) : null, children] }));
    const actions = (_jsx(DialogHandlerFooter, { footer: footer, destructiveAction: destructiveAction, secondaryActions: secondaryActions, cancelAction: cancelAction, primaryAction: primaryAction }));
    if (presentation === "page") {
        if (!open)
            return null;
        return (_jsxs("section", { className: cx("ui-dialog-page", `ui-dialog-page--${size}`), "data-presentation": "page", "aria-labelledby": pageTitleId, "aria-describedby": description ? pageDescriptionId : undefined, children: [_jsxs("div", { className: "ui-dialog__header", children: [_jsxs("div", { children: [_jsx("h2", { className: "ui-dialog__title", id: pageTitleId, children: title }), description ? (_jsx("p", { className: "ui-dialog__description", id: pageDescriptionId, children: description })) : null] }), _jsx(IconButton, { "aria-label": "Dialog schlie\u00DFen", onClick: () => onOpenChange(false), children: _jsx(X, { size: 20, "aria-hidden": "true" }) })] }), body, actions] }));
    }
    return (_jsx(DialogPrimitive.Root, { open: open, onOpenChange: onOpenChange, children: _jsxs(DialogPrimitive.Portal, { children: [_jsx(DialogPrimitive.Overlay, { className: "ui-dialog__overlay" }), _jsxs(DialogPrimitive.Content, { className: cx("ui-dialog", `ui-dialog--${size}`, presentation === "sheet" && "ui-dialog--sheet"), "data-presentation": presentation, onOpenAutoFocus: onOpenAutoFocus, onCloseAutoFocus: onCloseAutoFocus, children: [_jsxs("div", { className: "ui-dialog__header", children: [_jsxs("div", { children: [_jsx(DialogPrimitive.Title, { className: "ui-dialog__title", children: title }), description ? (_jsx(DialogPrimitive.Description, { className: "ui-dialog__description", children: description })) : null] }), _jsx(DialogPrimitive.Close, { asChild: true, children: _jsx(IconButton, { "aria-label": "Dialog schlie\u00DFen", children: _jsx(X, { size: 20, "aria-hidden": "true" }) }) })] }), body, actions] })] }) }));
}
export function ConfirmDialog({ cancelAction, confirmAction, destructive = false, ...props }) {
    return (_jsx(DialogHandler, { ...props, presentation: "dialog", destructiveAction: destructive ? confirmAction : undefined, cancelAction: cancelAction, primaryAction: destructive ? undefined : confirmAction }));
}
/** Compatibility layer for existing callers. */
export function Dialog(props) {
    return _jsx(DialogHandler, { ...props, presentation: "dialog" });
}
export const Tabs = TabsPrimitive.Root;
export const TabsList = forwardRef(function TabsList({ className, ...props }, ref) {
    return _jsx(TabsPrimitive.List, { ref: ref, className: cx("ui-tabs", className), ...props });
});
export const TabsTrigger = forwardRef(function TabsTrigger({ className, ...props }, ref) {
    return (_jsx(TabsPrimitive.Trigger, { ref: ref, className: cx("ui-tabs__trigger", className), ...props, "aria-controls": props["aria-controls"] }));
});
export const TabsContent = TabsPrimitive.Content;
export function DropdownMenu({ trigger, children, align = "end", modal = true, }) {
    return (_jsxs(DropdownMenuPrimitive.Root, { modal: modal, children: [_jsx(DropdownMenuPrimitive.Trigger, { asChild: true, children: trigger }), _jsx(DropdownMenuPrimitive.Portal, { children: _jsx(DropdownMenuPrimitive.Content, { className: "ui-menu", align: align, sideOffset: 8, children: children }) })] }));
}
export const DropdownMenuItem = forwardRef(function DropdownMenuItem({ className, ...props }, ref) {
    return (_jsx(DropdownMenuPrimitive.Item, { ref: ref, className: cx("ui-menu__item", className), ...props }));
});
export const DropdownMenuSeparator = DropdownMenuPrimitive.Separator;
export function Spinner({ label = "Inhalt wird geladen" }) {
    return (_jsxs("span", { className: "ui-spinner-wrap", role: "status", children: [_jsx("span", { className: "ui-spinner", "aria-hidden": "true" }), _jsx("span", { className: "ui-sr-only", children: label })] }));
}
export function Progress({ value, label }) {
    const safeValue = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;
    return (_jsxs("div", { className: "ui-progress-wrap", children: [label ? (_jsxs("div", { className: "ui-progress__label", children: [_jsx("span", { children: label }), _jsxs("span", { children: [safeValue, "%"] })] })) : null, _jsx("div", { className: "ui-progress", role: "progressbar", "aria-label": label ?? "Fortschritt", "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": safeValue, children: _jsx("span", { className: "ui-progress__value", style: { width: `${safeValue}%` } }) })] }));
}
export function Skeleton({ className }) {
    return _jsx("span", { className: cx("ui-skeleton", className), "aria-hidden": "true" });
}
export function VisuallyHidden({ children }) {
    return _jsx("span", { className: "ui-sr-only", children: children });
}
//# sourceMappingURL=index.js.map